import { RuleValidationStatus, type RuleValidationTargetType } from '@atlas/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { notification } from '@/notifications/notification-store'
import { type RuleValidationRun, ruleValidationApi } from '@/services/rule-validation.service'

/** Query keys for a target's runs and for one run's records. */
export const RULE_VALIDATION_RUNS_KEY = 'rule-validation-runs'
export const RULE_VALIDATION_RUN_KEY = 'rule-validation-run'

export const ruleValidationRunsKey = (targetType: RuleValidationTargetType, targetId: string) => [
	RULE_VALIDATION_RUNS_KEY,
	targetType,
	targetId,
]

export const ruleValidationRunKey = (requestId: string) => [RULE_VALIDATION_RUN_KEY, requestId]

/**
 * Statuses a run no longer moves out of.
 *
 * `sent_to_validator` is deliberately NOT terminal even though a stalled run can sit
 * there indefinitely: the run is genuinely unfinished, and showing it as finished
 * would be the one thing an audit surface must never do.
 */
const TERMINAL_STATUSES: ReadonlySet<RuleValidationStatus> = new Set([
	RuleValidationStatus.COMPLETED,
	RuleValidationStatus.FAILED,
])

const REFETCH_INTERVAL_MS = 5000

const isWaiting = (runs: RuleValidationRun[]) => runs.some((run) => !TERMINAL_STATUSES.has(run.status))

/**
 * A target's validation runs, newest first.
 *
 * A run is answered asynchronously by N independent evaluations, so the panel has to wait
 * for it to reach `completed`/`failed`: poll every 5s while any run is non-terminal,
 * stopping once all are.
 *
 * Polling is NOT skipped on a live realtime connection, unlike `useValidationAssets`.
 * `validation_run.completed` and `validation_run.failed` are absent from
 * `REALTIME_PUSH_EVENT_TYPES`, so no browser ever receives them; standing down on a
 * connection that will never carry the message left a finished run showing the previous
 * run's verdicts until a reload. Push here is a real option, but it costs the two event
 * types being pushable AND the requester's `userId` on the envelope the recipient filter
 * reads - and even then polling stays the floor, because a missed message on an audit
 * surface means stale evidence rather than a late refresh.
 */
export function useRuleValidationRuns(targetType: RuleValidationTargetType, targetId: string | null) {
	const query = useQuery<RuleValidationRun[]>({
		queryKey: ruleValidationRunsKey(targetType, targetId ?? ''),
		queryFn: () => ruleValidationApi.listForTarget(targetType, targetId as string),
		enabled: !!targetId,
		refetchInterval: (q) => (isWaiting(q.state.data ?? []) ? REFETCH_INTERVAL_MS : false),
	})

	return query
}

/**
 * One run with its per-rule audit records.
 *
 * Polls on the same terms as the list while the run itself is non-terminal, so a table
 * opened mid-run fills in as verdicts land rather than staying at `pending` until the
 * user navigates away.
 */
export function useRuleValidationRun(targetType: RuleValidationTargetType, targetId: string, requestId: string | null) {
	return useQuery({
		queryKey: ruleValidationRunKey(requestId ?? ''),
		queryFn: () => ruleValidationApi.get(targetType, targetId, requestId as string),
		enabled: !!requestId,
		refetchInterval: (q) => {
			const status = q.state.data?.status
			return status && !TERMINAL_STATUSES.has(status) ? REFETCH_INTERVAL_MS : false
		},
	})
}

/**
 * Request a validation run, optionally narrowed to the rules named.
 *
 * Invalidate-only, no optimistic patch: a create has nothing to patch, and inventing a
 * placeholder run would put a row in an audit surface that the server has not recorded.
 */
export function useRequestRuleValidation(targetType: RuleValidationTargetType, targetId: string) {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (ruleIds?: string[]) => ruleValidationApi.request(targetType, targetId, ruleIds),
		onSettled: () => {
			void qc.invalidateQueries({ queryKey: ruleValidationRunsKey(targetType, targetId) })
		},
	})
}

/**
 * The `ApplicableRulesCard` props that make its rules validatable: a per-rule request
 * control and the way to the target's recorded results.
 *
 * One hook rather than the same four lines on each surface, because the part worth
 * having a single copy of is the reporting - a request that fails has to say so, and a
 * host that forgot would leave a button that looks like it did nothing.
 */
export function useApplicableRulesValidation(targetType: RuleValidationTargetType, targetId: string) {
	const navigate = useNavigate()
	const request = useRequestRuleValidation(targetType, targetId)
	const resultsHref = `/rules/validation-results?targetType=${targetType}&targetId=${targetId}`

	const report = {
		onError: () => notification.error('Could not request validation. Please try again.'),
	}

	return {
		resultsHref,
		onViewResults: () => void navigate(resultsHref),
		onValidateRule: (ruleId: string) =>
			request.mutate([ruleId], {
				...report,
				onSuccess: () => notification.success('Validation requested — the verdict appears once the rule is answered.'),
			}),
		// `variables` is the last submitted argument; it survives the mutation settling, so
		// it only names rules in flight while the request actually is.
		validatingRuleIds: request.isPending ? request.variables : undefined,
		onValidateAll: () =>
			request.mutate(undefined, {
				...report,
				onSuccess: () => notification.success('Validation requested — verdicts appear as each rule is answered.'),
			}),
		// An all-rules request names no rule, which is exactly what distinguishes it from a
		// per-rule one still in flight.
		isValidatingAll: request.isPending && !request.variables,
	}
}
